import { useState, useEffect, useCallback, useRef } from "react";
import * as Auth from "@/lib/Auth";
import Authorization from "@/components/Authorization";
import { FormattedMessage } from "react-intl";
import type { ICategory } from "@/lib/backend/manufacturing2.generated.types";
import type { ICategoryItemExt } from "./CategoryCreate";

import CategoryCreateEditItemModal from "./CategoryCreateEditItemModal";
import CategoryItemsList from "./CategoryItemsList";
import ConfirmationModal from "@/components/ConfirmationModal";
import { translate } from "@/components/IntlProviderWrapper";
import { getBackend } from "@/lib/backend/Backend2";
import ErrorComponent from "@/components/ErrorComponent";

type Props = {
    selected: ICategory | null;
    onCategoryDelete: () => Promise<void>;
    onCategoryUpdate: (category: ICategory) => Promise<void>;
};

const CategoryDetails = ({ selected, onCategoryDelete, onCategoryUpdate }: Props) => {
    const [showCreateEditModal, setShowCreateEditModal] = useState(false);
    const [selectedCategoryItem, setSelectedCategoryItem] = useState<ICategoryItemExt | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [modalError, setModalError] = useState<string | null>(null);
    const mounted_ref = useRef(true);

    const category = selected;
    const can_edit = Auth.isInElevatedRole() && category !== null;

    // Clear error when category changes and only if the selected prop changes
    useEffect(() => {
        setError(null);
    }, [selected]);

    // Cleanup function to prevent memory leaks
    useEffect(() => {
        return () => {
            mounted_ref.current = false;
        };
    }, []);

    const handleAddCategoryItem = useCallback(() => {
        const new_category_item = {
            name: "",
            code: "",
            description: "",
            id: null,
        };
        setModalError(null);
        setError(null);
        setSelectedCategoryItem(new_category_item);
        setShowCreateEditModal(true);
    }, []);

    // Handle saving a category item with error handling and loading state
    const handleSaveCategoryItem = useCallback(
        async (category_item: ICategoryItemExt) => {
            if (!category) return;

            setIsLoading(true);
            setModalError(null);
            setError(null);

            try {
                const items = [...category.items];

                if (category_item.id === null) {
                    if (items.find(i => i.code === category_item.code)) {
                        setModalError(
                            translate("Categories.item_with_code_already_exists", "Item with code already exists")
                        );
                        return;
                    }
                    items.push({
                        code: category_item.code,
                        description: category_item.description,
                        name: category_item.name,
                        id: category_item.id,
                    });
                } else {
                    const index = items.findIndex(i => i.id === category_item.id);
                    if (index !== -1) {
                        items[index] = {
                            code: category_item.code,
                            description: category_item.description,
                            name: category_item.name,
                            id: category_item.id,
                        };
                    }
                }

                const res = await getBackend().manufacturing.updateCategory({
                    category_id: category.id,
                    category: {
                        ...category,
                        items: items.map(i => ({ ...i })),
                    },
                });

                if (res.category) {
                    // Refresh parent data to reflect changes in UI
                    await onCategoryUpdate(res.category);

                    if (mounted_ref.current) {
                        setSelectedCategoryItem(null);
                        setShowCreateEditModal(false);
                    }
                }
            } catch (err) {
                if (mounted_ref.current) {
                    setError(translate("common.error_save", "An error occurred while saving data"));
                }
            } finally {
                if (mounted_ref.current) {
                    setIsLoading(false);
                }
            }
        },
        [category]
    );

    const handleEditCategoryItem = useCallback((category_item: ICategoryItemExt) => {
        setModalError(null);
        setError(null);
        setSelectedCategoryItem(category_item);
        setShowCreateEditModal(true);
    }, []);

    const handleRemoveCategoryItem = useCallback(
        async (category_item: ICategoryItemExt) => {
            if (!category) return;

            setIsLoading(true);
            setError(null);

            try {
                const items = category.items.filter(i => i.id !== category_item.id);
                const res = await getBackend().manufacturing.updateCategory({
                    category_id: category.id,
                    category: {
                        ...category,
                        items,
                    },
                });

                // Refresh parent data to reflect changes in UI
                await onCategoryUpdate(res.category);
            } catch (err) {
                if (mounted_ref.current) {
                    setError(
                        translate(
                            "Categories.error_removing_category_item",
                            "Error occurred while removing category item"
                        )
                    );
                }
            } finally {
                if (mounted_ref.current) {
                    setIsLoading(false);
                }
            }
        },
        [category]
    );

    const handleHideCreateEditModal = useCallback(() => {
        setSelectedCategoryItem(null);
        setShowCreateEditModal(false);
    }, []);

    const handleOpenDeleteModal = useCallback(() => {
        setShowDeleteModal(true);
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
        setShowDeleteModal(false);
    }, []);

    const handleDeleteCategoryClick = useCallback(async () => {
        if (!category) return;

        if (mounted_ref.current) {
            setIsLoading(true);
            setError(null);
            setShowDeleteModal(false);
        }

        try {
            await getBackend().manufacturing.deleteCategory({
                category_id: category.id,
            });
            await onCategoryDelete();
        } catch (err) {
            if (mounted_ref.current) {
                setError(translate("Categories.error_removing_category", "Error occurred while removing category"));
            }
        } finally {
            if (mounted_ref.current) {
                setIsLoading(false);
            }
        }
    }, [category, onCategoryDelete]);

    if (!category) {
        return null;
    }

    const index =
        category.items.findIndex(
            item => item.id === selectedCategoryItem?.id && item.code === selectedCategoryItem?.code
        ) || -1;

    return (
        <div>
            <div className="tab-content">
                {error && <ErrorComponent msg={error} type="error" />}
                <div className="tab-pane active tab-pane-text">
                    <div className="d-flex">
                        {can_edit && (
                            <div className="button-list order-last ml-auto">
                                <Authorization.Link
                                    permission={Auth.PERMISSION_NAMES.CategoryEdit}
                                    className="btn btn-outline-secondary"
                                    to={`/digital-twin/resources/category/edit/${category.id}`}
                                    aria-label={translate("Categories.edit_category", "Edit category")}
                                >
                                    <FormattedMessage id="common.edit" defaultMessage="Edit" />
                                </Authorization.Link>
                                <Authorization.button
                                    permission={Auth.PERMISSION_NAMES.CategoryEdit}
                                    className="btn btn-outline-danger"
                                    onClick={handleOpenDeleteModal}
                                    disabled={isLoading}
                                    aria-label={translate("Categories.delete_category", "Delete category")}
                                >
                                    <FormattedMessage id="common.delete" defaultMessage="Delete" />
                                </Authorization.button>
                            </div>
                        )}
                        <div className="order-first">
                            <h5 className="pane-title">
                                <em>
                                    <FormattedMessage id="common.category" defaultMessage="Category" />
                                </em>
                                {category.name}
                            </h5>
                            <dl className="property-list">
                                <div>
                                    <dt>
                                        <FormattedMessage id="common.code" defaultMessage="Code" />
                                    </dt>
                                    <dd>
                                        <div>{category.code}</div>
                                    </dd>
                                </div>
                                <div>
                                    <dt className="light">
                                        <FormattedMessage id="common.id" defaultMessage="ID" />
                                    </dt>
                                    <dd className="light">
                                        <div>{category.id}</div>
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                    <CategoryItemsList
                        items={category.items}
                        title="Items"
                        canEdit={can_edit}
                        onAdd={handleAddCategoryItem}
                        onEdit={handleEditCategoryItem}
                        onRemove={handleRemoveCategoryItem}
                    />
                </div>
            </div>

            <CategoryCreateEditItemModal
                show={showCreateEditModal}
                onHide={handleHideCreateEditModal}
                onSave={handleSaveCategoryItem}
                category_item={selectedCategoryItem}
                error={modalError}
                index={index}
            />

            <ConfirmationModal
                title={translate("Categories.delete_category", "Delete category")}
                accept_label={translate("common.delete", "Delete")}
                cancel_label={translate("common.cancel", "Cancel")}
                dangerous={true}
                show={showDeleteModal}
                onClose={handleCloseDeleteModal}
                onAccept={handleDeleteCategoryClick}
                onCancel={handleCloseDeleteModal}
            >
                {translate(
                    "Categories.are_you_sure_delete",
                    "Are you sure you want to delete this category? All items will be deleted as well."
                )}
                <dl className="property-list alert alert-warning mt-2 mb-0">
                    <div>
                        <dt>{translate("common.code", "Code")}</dt>
                        <dd>{category.code}</dd>
                    </div>
                    <div>
                        <dt>{translate("common.name", "Name")}</dt>
                        <dd>{category.name}</dd>
                    </div>
                </dl>
            </ConfirmationModal>
        </div>
    );
};

export default CategoryDetails;
